import type { AlarmStatus } from '@empower-safety/shared-types';
import { Battery, CheckCircle2, Phone, ShieldAlert } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { type AlarmDto, useAcknowledgeAlarm, useAlarmsList } from '../api/useAlarms.js';
import { type RosterMember, useRoster } from '../api/useRoster.js';
import { EmptyState } from '../components/EmptyState.js';
import { StatusPill } from '../components/StatusPill.js';
import { cn } from '../lib/cn.js';
import { formatAlarmType, formatDateTime, formatRelative } from '../lib/format.js';

type Filter = 'all' | AlarmStatus;

const tabs: { key: Filter; label: string }[] = [
  { key: 'active', label: 'Active' },
  { key: 'acknowledged', label: 'Acknowledged' },
  { key: 'resolved', label: 'Resolved' },
  { key: 'all', label: 'All' },
];

export default function AlarmsList() {
  const [filter, setFilter] = useState<Filter>('active');
  // Pull a wider window so we can render counts per tab even when filtered to one
  // status. Cheap with a 200-row cap and we already cache it on the dashboard route.
  const allQuery = useAlarmsList({});
  const query = useAlarmsList(filter === 'all' ? {} : { status: filter });
  const ack = useAcknowledgeAlarm();
  // Roster lookup so each row can render "Display Name · VIC Physiotherapist" instead
  // of an opaque 8-char user id. Cheap — the dashboard caches this on the parent route.
  const roster = useRoster();
  const memberById = useMemo(() => {
    const map = new Map<string, RosterMember>();
    for (const m of roster.data?.members ?? []) map.set(m.user_id, m);
    return map;
  }, [roster.data]);

  const all = allQuery.data?.alarms ?? [];
  const counts: Record<Filter, number> = {
    all: all.length,
    active: all.filter((a) => a.status === 'active').length,
    acknowledged: all.filter((a) => a.status === 'acknowledged').length,
    resolved: all.filter((a) => a.status === 'resolved' || a.status === 'false_alarm').length,
    false_alarm: all.filter((a) => a.status === 'false_alarm').length,
  };

  // Triage order: open alarms first, and within them OLDEST first — the crisis
  // that's waited longest sits at the top, not the bottom. Resolved rows fall
  // back to most-recent-first.
  const statusRank = (s: AlarmStatus) => (s === 'active' ? 0 : s === 'acknowledged' ? 1 : 2);
  const alarms = useMemo(() => {
    const rows = query.data?.alarms ?? [];
    return [...rows].sort((a, b) => {
      const ra = statusRank(a.status);
      const rb = statusRank(b.status);
      if (ra !== rb) return ra - rb;
      const ta = new Date(a.triggered_at).getTime();
      const tb = new Date(b.triggered_at).getTime();
      return ra < 2 ? ta - tb : tb - ta;
    });
  }, [query.data]);

  return (
    <div className="mx-auto max-w-6xl">
      <div className="mb-5 flex items-end justify-between gap-4">
        <div>
          <span className="eyebrow">Operations</span>
          <h1 className="mt-1 font-heading text-[22px] font-bold tracking-tightest text-ink">
            Alarms
          </h1>
          <p className="text-sm text-ink-faint">
            {query.isFetching ? 'Refreshing…' : 'Auto-refreshes every 15 s.'}
          </p>
        </div>
        <div className="inline-flex gap-0.5 rounded-md border border-line bg-surface-1 p-1 shadow-xs">
          {tabs.map((t) => {
            const isActive = filter === t.key;
            const count = counts[t.key];
            const showDanger = t.key === 'active' && count > 0;
            return (
              <button
                key={t.key}
                type="button"
                onClick={() => setFilter(t.key)}
                className={cn(
                  'rounded-sm px-3.5 py-1.5 text-xs font-semibold transition',
                  isActive ? 'bg-brand-purple text-white' : 'text-ink-faint hover:text-ink',
                )}
              >
                {t.label}
                <span
                  className={cn(
                    'ml-2 rounded-full px-1.5 py-px font-mono text-[10px] font-bold',
                    isActive
                      ? 'bg-white/20 text-white'
                      : showDanger
                        ? 'bg-danger-soft text-danger'
                        : 'bg-surface-3 text-ink-faint',
                  )}
                >
                  {count}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {query.isError && (
        <div className="mb-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          Failed to load alarms: {(query.error as Error).message}
        </div>
      )}

      {!query.isLoading && alarms.length === 0 && (
        <EmptyState
          icon={CheckCircle2}
          title="No alarms in this view"
          description={
            filter === 'active'
              ? 'All quiet on your patch right now. Triggered alarms will appear here in real time.'
              : 'Change the filter or trigger a test alarm from the mobile app to see entries.'
          }
        />
      )}

      {alarms.length > 0 && (
        <div className="panel">
          <table className="min-w-full divide-y divide-line">
            <thead className="bg-surface-2">
              <tr>
                <Th>Status</Th>
                <Th>Type</Th>
                <Th>Triggered</Th>
                <Th>Worker</Th>
                <Th>Contact</Th>
                <Th className="text-right">Actions</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line bg-surface-1">
              {alarms.map((a) => (
                <AlarmRow
                  key={a.id}
                  alarm={a}
                  member={memberById.get(a.user_id) ?? null}
                  onAck={(id) => ack.mutate(id)}
                  acking={ack.isPending}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function AlarmRow({
  alarm,
  member,
  onAck,
  acking,
}: {
  alarm: AlarmDto;
  member: RosterMember | null;
  onAck: (id: string) => void;
  acking: boolean;
}) {
  const badge = [member?.state, member?.job_title].filter(Boolean).join(' ');
  // Active rows get a soft danger-tinted left blend, matching the design's table.urgent
  // pattern. Hover deepens the blend slightly so it stays legible.
  const isUrgent = alarm.status === 'active';
  // An active alarm still unacknowledged after 15 min is a red flag — colour its age.
  const staleActive = isUrgent && Date.now() - new Date(alarm.triggered_at).getTime() > 15 * 60_000;
  return (
    <tr
      className={cn(
        'cursor-pointer transition',
        isUrgent
          ? 'bg-gradient-to-r from-danger-soft to-transparent to-60% hover:from-[#fbe0d3]'
          : 'hover:bg-surface-2',
      )}
    >
      <Td>
        <StatusPill status={alarm.status} />
      </Td>
      <Td>
        <div className="flex items-center gap-2">
          <ShieldAlert
            className={cn('h-4 w-4', alarm.type === 'duress' ? 'text-danger' : 'text-warning')}
          />
          <span className="font-semibold text-ink">{formatAlarmType(alarm.type)}</span>
        </div>
      </Td>
      <Td>
        <div className={cn(staleActive ? 'font-semibold text-danger' : 'text-ink-dim')}>
          {formatRelative(alarm.triggered_at)}
        </div>
        <div className="font-mono text-[11px] text-ink-mute">
          {formatDateTime(alarm.triggered_at)}
        </div>
      </Td>
      <Td>
        {member ? (
          <>
            <div className="text-sm font-medium text-ink">{member.display_name}</div>
            {badge && <div className="text-[11px] text-ink-mute">{badge}</div>}
          </>
        ) : (
          <span className="font-mono text-[11.5px] text-ink-faint">
            {alarm.user_id.slice(0, 8)}…
          </span>
        )}
        {/* Battery demoted from its own column to a small chip on the worker. */}
        {alarm.device_battery != null && (
          <span
            className={cn(
              'mt-0.5 inline-flex items-center gap-0.5 font-mono text-[11px] font-semibold',
              alarm.device_battery < 20
                ? 'text-danger'
                : alarm.device_battery < 40
                  ? 'text-warning'
                  : 'text-ink-faint',
            )}
            title="Device battery at trigger"
          >
            <Battery className="h-3 w-3" />
            {alarm.device_battery}%
          </span>
        )}
      </Td>
      <Td>
        {member?.phone_e164 ? (
          <a
            href={`tel:${member.phone_e164}`}
            className="inline-flex items-center gap-1.5 rounded-md border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-800 transition hover:bg-emerald-100"
            title={`Call ${member.display_name}`}
          >
            <Phone className="h-3.5 w-3.5" />
            {member.phone_e164}
          </a>
        ) : (
          <span className="text-sm text-ink-mute">No phone</span>
        )}
      </Td>
      <Td className="text-right">
        <div className="flex items-center justify-end gap-2">
          <Link
            to={`/alarms/${alarm.id}`}
            className="rounded-md border border-line bg-surface-1 px-3 py-1 text-xs font-semibold text-ink-dim transition hover:border-line-strong hover:bg-surface-3 hover:text-ink"
          >
            Details
          </Link>
          {alarm.status === 'active' && (
            <button
              type="button"
              onClick={() => onAck(alarm.id)}
              disabled={acking}
              className="rounded-md bg-brand-purple px-3 py-1 text-xs font-semibold text-white shadow-glow-primary transition hover:bg-brand-purple/90 disabled:opacity-50"
            >
              Acknowledge
            </button>
          )}
        </div>
      </Td>
    </tr>
  );
}

function Th({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <th
      scope="col"
      className={cn(
        'px-4 py-3 text-left font-mono text-[9.5px] font-bold uppercase tracking-eyebrow text-ink-mute',
        className,
      )}
    >
      {children}
    </th>
  );
}

function Td({ children, className }: { children: React.ReactNode; className?: string }) {
  return <td className={cn('px-4 py-3 text-[13px] align-middle', className)}>{children}</td>;
}
